import { OutlinedInput, InputAdornment, Stack, Chip, Button } from "@mui/material";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import { DbStorageInfos } from "../../../../Core/Api/Types/db-storage-infos";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import { setupService } from "../../../../Core/Api/services";
import { ModalContext } from "../../../../Components/Modals";
import ModalStorageEdit from "../../../../Components/Modals/ModalStorageEdit/modal-storage-edit";
import { DbFormatInfos } from "../../../../Core/Api/Types/db-format-infos";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import AssetTypeChip from "../../../../Components/Misc/Chips/AssetChip/asset-type-chip";
import ModalFormatEdit from "../../../../Components/Modals/ModalFormatEdit/modal-format-edit";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";
import { GetEnumValueLabelMap } from "../../../../Utils/helpers";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { ObjectClass } from "../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";

function AdminSettingsFormats() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const modalContext = useContext(ModalContext);

  const { openModal } = { ...modalContext };

  const [rows, setRows] = useState<DbFormatInfos[]>([]);

  useEffect(() => {
    setupService.GetFormats().then((data) => {
      setRows(data.formats ?? []);
      setIsLoading(false);
    });
  }, []);

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
      },
      {
        field: "name",
        headerName: t("common:name"),
        flex: CellSize.small,
        minWidth: CellSize.small,
      },
      {
        field: "isSystem",
        headerName: t("common:system"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        field: "isDownloadable",
        headerName: t("common:download"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        field: "isUploadable",
        headerName: t("common:upload"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        field: "isDeletable",
        headerName: t("common:delete"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        field: "isRestricted",
        headerName: t("table:headerName.restricted"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        field: "enableParsing",
        headerName: t("table:headerName.parsing"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        field: "usage",
        headerName: t("common:usage"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (data: Row) => <Chip label={data.value} />,
      },
      {
        field: "objectType",
        headerName: t("table:headerName.objectType"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectType),
        renderCell: (data: Row) => <AssetTypeChip type={data.value} />,
      },
      {
        field: "linkedType",
        headerName: t("table:headerName.linkedType"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data: Row) => <Chip label={data.value ?? "-"} />,
      },
      {
        resizable: true,
        field: "objectClasses",
        headerName: t("field:label.objectClassesLowerCase"),
        flex: CellSize.large,
        minWidth: CellSize.large,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectClass),
        renderCell: (data: Row) => <TableCellChip data={data} />,
      },
      {
        resizable: true,
        field: "objectLayers",
        headerName: t("table:headerName.objectLayers"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectLayer),
        renderCell: (data: Row) => <TableCellChip data={data} />,
      },
      {
        resizable: true,
        field: "fileExtensions",
        headerName: t("field:label.fileExtensions"),
        flex: CellSize.large,
        minWidth: CellSize.large,
        renderCell: (data: Row) => <TableCellChip data={{ value: data.value?.map((x) => x.fileExtension) }} />,
      },
      {
        headerName: "",
        field: "actions",
        type: "actions",
        align: "right",
        flex: CellSize.actionsSmall,
        minWidth: CellSize.actionsSmall,
        getActions: (params: GridRowParams) => {
          if (!params || !params.row) return;
          return [
            <GridActionsCellItem
              icon={<ModeEditOutlineOutlinedIcon />}
              label={t("common:editUpperCase")}
              onClick={() => openFormatEditModal(false, params.row)}
            />,
          ];
        },
      },
    ],
    [rows]
  );

  const openFormatEditModal = useCallback(
    (isCreation: boolean, format: DbFormatInfos) => {
      let feedbackData = isCreation
        ? (newFormat) => setRows([...rows, newFormat])
        : (updatedFormat) => setRows([...rows.filter((x) => x.id != format.id), updatedFormat]);

      if (!openModal) return;
      openModal(ModalFormatEdit, { isCreation, format: format, feedbackData });
    },
    [openModal, rows]
  );

  const additionalActions = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      sx={{ ml: 1 }}
      onClick={() => {
        openFormatEditModal(true, new DbFormatInfos());
      }}
    >
      {t("button:newFormat")}
    </Button>
  );

  const Table = useMemo(
    () => (
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.SETTINGS_FORMATS}
        columns={columns}
        rows={rows}
        additionalActions={additionalActions}
        rowHeight={64}
        pagination={true}
        disableRowSelectionOnClick
        // apiRef={apiGrid}
        getRowHeight={() => "auto"}
      />
    ),
    [rows, isLoading]
  );

  return <>{Table}</>;
}

export default AdminSettingsFormats;
